import React, { useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatMoney } from "../lib/constants";
import { Brain, Share2, Download, X, Copy, Send, Linkedin, MessageCircle } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const TAGLINE_PRESETS = [
  { key: "lesson", value: "Lesson learned." },
  { key: "best", value: "Best week ever." },
  { key: "plan", value: "Back to the plan." },
  { key: "custom", value: "" }, // custom: user types
];

/**
 * Renders a 1080x1080 share card based on weekly stats + top emotion.
 * Hidden in normal flow until export trigger; captured via html2canvas.
 */
function ShareCardCanvas({ stats, currency, user, subject, isWarning, refSet, tagline }) {
  const pnlField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
  const totalField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
  const totalPnl = stats?.[totalField] ?? 0;
  const subjPnl = subject ? subject[pnlField] : 0;

  return (
    <div
      ref={refSet}
      data-testid="share-card-canvas"
      // 1080×1080 — kept off-screen via parent wrapper
      style={{
        width: "1080px",
        height: "1080px",
        background: "#09090B",
        color: "#F8FAFC",
        fontFamily: "'IBM Plex Sans', sans-serif",
        padding: "72px",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* Subtle grid background */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", position: "relative" }}>
        <div
          style={{
            width: "56px", height: "56px", background: "#00E5A0",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: "4px",
          }}
        >
          <span style={{ fontSize: "32px", color: "#000", fontWeight: 900, fontFamily: "Chivo, sans-serif" }}>
            B
          </span>
        </div>
        <span style={{
          fontFamily: "Chivo, sans-serif", fontSize: "36px", fontWeight: 700, letterSpacing: "-0.02em",
        }}>
          Brain<span style={{ color: "#00E5A0" }}>TRADES</span>
        </span>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <p style={{
            margin: 0, fontSize: "14px", textTransform: "uppercase",
            letterSpacing: "0.25em", color: "#A1A1AA", fontWeight: 600,
          }}>
            Weekly Recap
          </p>
          <p style={{ margin: "6px 0 0", fontFamily: "monospace", fontSize: "16px", color: "#A1A1AA" }}>
            {stats?.week_start} → {new Date().toISOString().slice(0, 10)}
          </p>
        </div>
      </div>

      {/* Headline */}
      <div style={{ marginTop: "72px", position: "relative" }}>
        <p style={{
          margin: 0, fontSize: "16px", textTransform: "uppercase",
          letterSpacing: "0.3em", fontWeight: 700,
          color: isWarning ? "#FF3B30" : "#00E5A0",
        }}>
          {isWarning ? "Pattern of the week · Watch out" : "Pattern of the week"}
        </p>
        <h1 style={{
          margin: "20px 0 0", fontFamily: "Chivo, sans-serif",
          fontSize: "78px", fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.03em",
        }}>
          {subject ? (
            isWarning ? (
              <>
                <span style={{ color: isWarning ? "#FF3B30" : "#00E5A0" }}>{subject.source}</span><br />
                cost me{" "}
                <span style={{ color: "#FF3B30" }}>
                  {formatMoney(subjPnl, currency)}
                </span>
              </>
            ) : (
              <>
                <span style={{ color: "#00E5A0" }}>{subject.source}</span><br />
                won me{" "}
                <span style={{ color: "#00E5A0" }}>
                  {formatMoney(subjPnl, currency)}
                </span>
              </>
            )
          ) : (
            <>No trades<br />logged this week</>
          )}
        </h1>
        {tagline && tagline.trim() && (
          <p
            data-testid="share-card-tagline"
            style={{
              margin: "24px 0 0",
              fontFamily: "Chivo, sans-serif",
              fontSize: "32px",
              fontStyle: "italic",
              fontWeight: 400,
              color: "#A1A1AA",
              borderLeft: `3px solid ${isWarning ? "#FF3B30" : "#00E5A0"}`,
              paddingLeft: "20px",
              lineHeight: 1.3,
            }}
          >
            "{tagline.trim()}"
          </p>
        )}
      </div>

      {/* Stat grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "28px",
        marginTop: "72px",
        position: "relative",
      }}>
        {[
          {
            label: "Week P&L",
            value: formatMoney(totalPnl, currency),
            color: totalPnl >= 0 ? "#00E5A0" : "#FF3B30",
          },
          {
            label: "Win Rate",
            value: `${(stats?.win_rate ?? 0).toFixed(2)}%`,
            color: (stats?.win_rate ?? 0) >= 50 ? "#00E5A0" : "#F8FAFC",
          },
          {
            label: subject ? `Avg R:R · ${subject.emotion}` : "Total Trades",
            value: subject?.avg_r_multiple != null
              ? `1 : ${subject.avg_r_multiple.toFixed(1)}`
              : `${stats?.total_trades ?? 0}`,
            color: subject?.avg_r_multiple != null
              ? (subject.avg_r_multiple >= 0 ? "#00E5A0" : "#FF3B30")
              : "#F8FAFC",
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              border: "1px solid #27272A",
              padding: "28px 24px",
              borderRadius: "4px",
              background: "#121214",
            }}
          >
            <p style={{
              margin: 0, fontSize: "13px", letterSpacing: "0.2em",
              textTransform: "uppercase", color: "#A1A1AA", fontWeight: 600,
            }}>
              {s.label}
            </p>
            <p style={{
              margin: "16px 0 0",
              fontFamily: "'JetBrains Mono', monospace",
              fontVariantNumeric: "tabular-nums",
              fontSize: "52px", fontWeight: 700,
              color: s.color, lineHeight: 1,
            }}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{
        position: "absolute", left: "72px", right: "72px", bottom: "60px",
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      }}>
        <div>
          <p style={{
            margin: 0, fontSize: "13px", letterSpacing: "0.25em",
            textTransform: "uppercase", color: "#A1A1AA", fontWeight: 600,
          }}>
            Journaled by
          </p>
          <p style={{
            margin: "8px 0 0",
            fontFamily: "Chivo, sans-serif",
            fontSize: "28px", fontWeight: 700,
          }}>
            {user?.name || "BrainTRADES Trader"}
          </p>
        </div>
        <p style={{
          margin: 0,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "16px", color: "#A1A1AA",
          textAlign: "right",
        }}>
          {subject ? `${subject.count} ${subject.source} trade${subject.count === 1 ? "" : "s"} · ${(subject.win_rate ?? 0).toFixed(2)}% win` : ""}
          <br />
          braintrades.ai
        </p>
      </div>
    </div>
  );
}

export default function ShareCardDialog({ open, onOpenChange, stats, currency }) {
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  // Iter 51 — track which share action is in flight (copy/native).
  const [actioning, setActioning] = useState(null);
  // Tagline picker
  const [taglineKey, setTaglineKey] = useState("none");
  const [customTagline, setCustomTagline] = useState("");

  const list = stats?.weekly_source_stats || [];
  const pnlField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
  let subject = null;
  let isWarning = false;
  if (list.length) {
    const worst = list[0];
    const best = list[list.length - 1];
    isWarning = worst[pnlField] < 0;
    subject = isWarning ? worst : best;
  }

  const activeTagline = (() => {
    if (taglineKey === "none") return "";
    if (taglineKey === "custom") return customTagline;
    const preset = TAGLINE_PRESETS.find((p) => p.key === taglineKey);
    return preset ? preset.value : "";
  })();

  // Generate the PNG blob once; cached for reuse across actions in the same session.
  const blobCacheRef = useRef({ key: null, blob: null });
  const renderToBlob = async () => {
    if (!canvasRef.current) throw new Error("Canvas not ready");
    // Cache key changes whenever the share-card content does (tagline, subject).
    const cacheKey = JSON.stringify({
      t: activeTagline,
      s: subject?.source || null,
      w: stats?.week_start,
    });
    if (blobCacheRef.current.key === cacheKey && blobCacheRef.current.blob) {
      return blobCacheRef.current.blob;
    }
    // Wait a tick for layout
    await new Promise((r) => setTimeout(r, 50));
    const canvas = await html2canvas(canvasRef.current, {
      backgroundColor: "#09090B",
      scale: 1,
      useCORS: true,
      logging: false,
    });
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    blobCacheRef.current = { key: cacheKey, blob };
    return blob;
  };

  // Build a default share message (text) for X/WhatsApp/LinkedIn — these
  // platforms accept text + URL only; users paste the copied image separately.
  const buildShareText = () => {
    const totalField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
    const totalPnl = stats?.[totalField] ?? 0;
    const wr = (stats?.win_rate ?? 0).toFixed(2);
    const wkLabel = stats?.week_start ? `Week of ${stats.week_start}` : "This week";
    const headline = subject
      ? (isWarning
          ? `${subject.source} cost me ${formatMoney(subject[pnlField] || 0, currency)}.`
          : `${subject.source} won me ${formatMoney(subject[pnlField] || 0, currency)}.`)
      : `${stats?.total_trades ?? 0} trades logged.`;
    return `📊 ${wkLabel} on BrainTRADES — Net ${formatMoney(totalPnl, currency)} · Win rate ${wr}%. ${headline} Building my edge one trade at a time.`;
  };

  const APP_URL = "https://braintrades.ai";

  const onDownload = async () => {
    setDownloading(true);
    try {
      const blob = await renderToBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `braintrades_weekly_${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Share card downloaded");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[ShareCardDialog] PNG generation failed:", err);
      }
      toast.error("Failed to generate PNG");
    } finally {
      setDownloading(false);
    }
  };

  // Iter 51 — Copy image to clipboard so user can paste straight into X / Slack / etc.
  const onCopyImage = async () => {
    setActioning("copy");
    try {
      const blob = await renderToBlob();
      // ClipboardItem requires a Promise<Blob> in some browsers
      const item = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([item]);
      toast.success("Image copied — paste it anywhere");
    } catch (err) {
      // Clipboard image write isn't supported in all browsers (Firefox/Safari iOS).
      // Fall back to copying the share text instead.
      try {
        await navigator.clipboard.writeText(`${buildShareText()} ${APP_URL}`);
        toast.success("Image copy not supported here — text copied instead");
      } catch {
        toast.error("Couldn't copy. Try Download PNG and attach manually.");
      }
    } finally {
      setActioning(null);
    }
  };

  // Iter 51 — Native share sheet (mobile-first; also works on macOS Safari).
  // Uses navigator.share with files when supported; falls back to text-only share.
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const onNativeShare = async () => {
    setActioning("native");
    try {
      const blob = await renderToBlob();
      const file = new File(
        [blob],
        `braintrades_weekly_${new Date().toISOString().slice(0, 10)}.png`,
        { type: "image/png" }
      );
      const text = buildShareText();
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "BrainTRADES Weekly Recap" });
      } else {
        // Files unsupported (e.g. desktop Chrome) — share text+url only.
        await navigator.share({ text: `${text} ${APP_URL}`, title: "BrainTRADES Weekly Recap" });
      }
    } catch (err) {
      // AbortError = user cancelled, not a failure.
      if (err && err.name !== "AbortError") {
        toast.error("Native share unavailable here");
      }
    } finally {
      setActioning(null);
    }
  };

  // Iter 51 — Intent links. Image must be pasted by the user (after copy or download)
  // because none of X / WhatsApp / LinkedIn accept images via URL params.
  const openIntent = (kind) => {
    const text = buildShareText();
    const enc = encodeURIComponent;
    let url;
    if (kind === "x") {
      url = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(APP_URL)}`;
    } else if (kind === "whatsapp") {
      url = `https://wa.me/?text=${enc(`${text} ${APP_URL}`)}`;
    } else if (kind === "linkedin") {
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(APP_URL)}`;
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[var(--bt-surface)] border-[var(--bt-border)]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[var(--bt-accent)]" />
            Weekly Recap Card
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--bt-text-muted)]">
            Download as a 1080×1080 PNG to share on X, Discord, or wherever traders gather.
          </DialogDescription>
        </DialogHeader>

        {/* Visible preview — scaled to fit */}
        <div
          data-testid="share-card-preview"
          className="border border-[var(--bt-border)] rounded-sm overflow-hidden bg-black flex items-center justify-center"
          style={{ aspectRatio: "1/1" }}
        >
          <div style={{ transform: "scale(0.45)", transformOrigin: "center" }}>
            <ShareCardCanvas
              stats={stats}
              currency={currency}
              user={user}
              subject={subject}
              isWarning={isWarning}
              refSet={() => {}}
              tagline={activeTagline}
            />
          </div>
        </div>

        {/* Tagline picker */}
        <div data-testid="tagline-picker" className="space-y-2">
          <Label className="bt-label">Optional tagline</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "none", label: "No tagline" },
              { key: "lesson", label: "Lesson learned" },
              { key: "best", label: "Best week ever" },
              { key: "plan", label: "Back to the plan" },
              { key: "custom", label: "Custom…" },
            ].map((opt) => {
              const active = taglineKey === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`tagline-option-${opt.key}`}
                  onClick={() => setTaglineKey(opt.key)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition-colors duration-150 ${
                    active
                      ? "bg-[var(--bt-accent)] text-black border-[var(--bt-accent)]"
                      : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:text-white hover:border-zinc-500"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {taglineKey === "custom" && (
            <Input
              autoFocus
              value={customTagline}
              onChange={(e) => setCustomTagline(e.target.value.slice(0, 80))}
              placeholder="Type your tagline (max 80 chars)…"
              data-testid="tagline-custom-input"
              maxLength={80}
              className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-10 rounded-sm"
            />
          )}
        </div>

        {/* Hidden off-screen render at full size — used by html2canvas */}
        <div style={{
          position: "fixed", left: "-99999px", top: 0, pointerEvents: "none",
        }}>
          <ShareCardCanvas
            stats={stats}
            currency={currency}
            user={user}
            subject={subject}
            isWarning={isWarning}
            refSet={(el) => { canvasRef.current = el; }}
            tagline={activeTagline}
          />
        </div>

        {/* Iter 51 — Share intents row (X / WhatsApp / LinkedIn).
            Images can't be attached via URL params on these platforms,
            so we open the intent with pre-filled text + tip to paste the
            copied image. */}
        <div
          data-testid="share-card-intents"
          className="flex flex-wrap gap-2 pt-2 border-t border-[var(--bt-border)]"
        >
          <p className="w-full text-xs text-[var(--bt-text-muted)] mb-1">
            Tip: copy the image first, then paste into the post that opens.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => openIntent("x")}
            data-testid="share-intent-x"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] rounded-sm gap-1.5"
          >
            <Send className="w-3.5 h-3.5" /> Post on X
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => openIntent("whatsapp")}
            data-testid="share-intent-whatsapp"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] rounded-sm gap-1.5"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => openIntent("linkedin")}
            data-testid="share-intent-linkedin"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] rounded-sm gap-1.5"
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="share-card-close-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-[var(--bt-text)] rounded-sm"
          >
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCopyImage}
            disabled={actioning === "copy"}
            data-testid="share-card-copy-btn"
            className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-[var(--bt-text)] rounded-sm"
          >
            <Copy className="w-4 h-4 mr-1.5" />
            {actioning === "copy" ? "Copying…" : "Copy image"}
          </Button>
          {canNativeShare && (
            <Button
              type="button"
              variant="outline"
              onClick={onNativeShare}
              disabled={actioning === "native"}
              data-testid="share-card-native-btn"
              className="border-[var(--bt-border)] bg-transparent hover:bg-[var(--bt-surface-hover)] text-[var(--bt-text)] rounded-sm"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              {actioning === "native" ? "Opening…" : "Share…"}
            </Button>
          )}
          <Button
            onClick={onDownload}
            disabled={downloading}
            data-testid="share-card-download-btn"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Generating…" : "Download PNG"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
